var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import parksJSON from './parks.json';
import { HolidayType, } from './types';
export const PARKS = parksJSON;
let cachedParksMap;
let cachedRegionsMap;
// General
export const getParks = () => PARKS;
export const getParksMap = () => {
    if (cachedParksMap)
        return cachedParksMap;
    cachedParksMap = new Map(PARKS.map((park) => [park.code, park]));
    return cachedParksMap;
};
export const getRegions = (type) => {
    return PARKS.reduce((acc, park) => {
        var _a;
        if (type && !((_a = park[type]) === null || _a === void 0 ? void 0 : _a.available)) {
            return acc;
        }
        const { holiday, touring, ownership } = park, restOfPark = __rest(park, ["holiday", "touring", "ownership"]);
        const transformedPark = type ? Object.assign(Object.assign({}, restOfPark), { [type]: park[type] }) : park;
        const regionIndex = acc.findIndex(({ code }) => code === transformedPark.region.code);
        if (regionIndex === -1) {
            return [
                ...acc,
                {
                    code: transformedPark.region.code,
                    name: transformedPark.region.name,
                    parkCodes: [transformedPark.code],
                    parks: [transformedPark],
                },
            ];
        }
        acc[regionIndex].parkCodes.push(transformedPark.code);
        acc[regionIndex].parks.push(transformedPark);
        return acc;
    }, []);
};
export const getRegionsMap = () => {
    if (cachedRegionsMap)
        return cachedRegionsMap;
    cachedRegionsMap = new Map(getRegions().map((region) => [region.code, region]));
    return cachedRegionsMap;
};
// Park options group for dropdowns in form
export const getParkOptionsGroup = (key) => getRegions().reduce((acc, region) => [
    ...acc,
    {
        label: region.name,
        options: region.parkCodes.reduce((acc, code) => {
            const park = getParksMap().get(code);
            if (!park || !park[key].available)
                return acc;
            return [
                ...acc,
                {
                    value: park.code,
                    label: park.name,
                },
            ];
        }, []),
    },
], []);
export const getVisitableOwnershipParkOptionsGroup = () => getRegions().reduce((acc, region) => [
    ...acc,
    {
        label: region.name,
        options: region.parkCodes.reduce((acc, code) => {
            const park = getOwnershipParksMap().get(code);
            if (!park || !park.available || park.availableToVisit === false)
                return acc;
            return [
                ...acc,
                {
                    value: park.code,
                    label: park.name,
                },
            ];
        }, []),
    },
], []);
export const getFlatParks = (key) => PARKS.reduce((acc, park) => {
    const { touring, holiday, ownership } = park, rest = __rest(park, ["touring", "holiday", "ownership"]);
    if (!park[key].available)
        return acc;
    return [
        ...acc,
        Object.assign(Object.assign({}, rest), park[key]),
    ];
}, []);
export const getFlatParksMap = (key) => {
    const parks = getFlatParks(key);
    return new Map(parks.map((park) => [park.code, park]));
};
export const getFlatRegions = (key) => getRegions().map((_a) => {
    var { parks, parkCodes } = _a, region = __rest(_a, ["parks", "parkCodes"]);
    const filteredParks = parks.filter((park) => park.ownership.available);
    return Object.assign(Object.assign({}, region), { parks: filteredParks.map((park) => {
            const { touring, holiday, ownership } = park, rest = __rest(park, ["touring", "holiday", "ownership"]);
            return Object.assign(Object.assign({}, rest), park[key]);
        }), parkCodes: filteredParks.map(({ code }) => code) });
});
export const getParksMapByType = (type) => {
    if (type === HolidayType.TOURING)
        return getTouringParksMap();
    if (type === HolidayType.OWNERSHIP)
        return getOwnershipParksMap();
    return getHolidaysParksMap();
};
export const getParkByCode = (type, parkCode) => getParksMapByType(type).get(parkCode);
// Ownership
let cachedOwnershipParks;
let cachedOwnershipParksMap;
let cachedOwnershipRegions;
export const getOwnershipParks = () => {
    if (cachedOwnershipParks)
        return cachedOwnershipParks;
    cachedOwnershipParks = getFlatParks('ownership');
    return cachedOwnershipParks;
};
export const getOwnershipParksMap = () => {
    if (cachedOwnershipParksMap)
        return cachedOwnershipParksMap;
    cachedOwnershipParksMap = getFlatParksMap('ownership');
    return cachedOwnershipParksMap;
};
export const getNumberOfOwnershipParks = () => getOwnershipParksMap().size;
export const getOwnershipRegions = () => {
    if (cachedOwnershipRegions)
        return cachedOwnershipRegions;
    cachedOwnershipRegions = getFlatRegions('ownership');
    return cachedOwnershipRegions;
};
// Holidays
let cachedHolidaysParks;
let cachedHolidaysParksMap;
export const getHolidaysParks = () => {
    if (cachedHolidaysParks)
        return cachedHolidaysParks;
    cachedHolidaysParks = getFlatParks('holiday');
    return cachedHolidaysParks;
};
export const getHolidaysParksMap = () => {
    if (cachedHolidaysParksMap)
        return cachedHolidaysParksMap;
    cachedHolidaysParksMap = getFlatParksMap('holiday');
    return cachedHolidaysParksMap;
};
export const getNumberOfHolidaysParks = () => getHolidaysParksMap().size;
// Touring
let cachedTouringParks;
let cachedTouringParksMap;
export const getTouringParks = () => {
    if (cachedTouringParks)
        return cachedTouringParks;
    cachedTouringParks = getFlatParks('touring');
    return cachedTouringParks;
};
export const getTouringParksMap = () => {
    if (cachedTouringParksMap)
        return cachedTouringParksMap;
    cachedTouringParksMap = getFlatParksMap('touring');
    return cachedTouringParksMap;
};
export const getNumberOfTouringParks = () => getTouringParksMap().size;
